import { type PropsWithChildren, useCallback, useMemo } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Loader } from '@opendoor/atoms/loader';
import type { MarketplaceBrowseQuery } from 'components/marketplace/browse/__generated__/marketplace-browse.zeus';
import { centsToDollars } from 'components/marketplace/helpers/money';
import type { MarketplaceFavoriteChangeHandler } from 'components/marketplace/helpers/useMarketplaceFavorite';
import { PaginationActions } from 'components/marketplace/map/PaginationActions';
import { ShowMore } from 'components/marketplace/map/ShowMore';
import { toDwellingType } from 'components/marketplace/property/dwellingType';
import { ListingCard, type PhotoLoadMode } from 'components/marketplace/property/SimilarListings';
import type { ListingCardData } from 'components/marketplace/property/types';
import type { LikedPropertyIds } from 'components/marketplace/types';

type MarketplacePropertyList = MarketplaceBrowseQuery['marketplace']['listings'];
type MarketplacePropertyListItem = MarketplacePropertyList['listings'][number];

const listingCardData = (
  listing: MarketplacePropertyListItem,
  likedPropertyIds?: LikedPropertyIds,
): ListingCardData => ({
  addressToken: listing.addressId,
  baths: listing.bathrooms,
  beds: listing.bedrooms,
  canSelfTour: listing.canSelfTour,
  city: listing.city,
  dwellingType: toDwellingType(listing.dwellingType),
  fullAddress: listing.fullAddress,
  listPrice: centsToDollars(listing.listPriceCents),
  listingState: listing.listingState,
  livingSquareFootage: listing.totalLivingSquareFootage,
  neighborhood: listing.neighborhood,
  photos: listing.photoUrls,
  isLiked: likedPropertyIds == null ? null : likedPropertyIds.has(listing.addressId),
  slug: listing.slug,
  state: listing.state,
  street: listing.street,
});

/**
 * How the list offers the rest of the result set. `showMore` appends the next
 * page in place and leaves the address bar alone; `pages` swaps one page for
 * another behind a real per-page URL, so every page is linkable and crawlable.
 */
type ListingPaginationMode =
  | { kind: 'showMore' }
  | {
      kind: 'pages';
      /** One-based, matching the URL. */
      currentPage: number;
      totalPages: number;
      hrefForPage: (page: number) => string;
      onPageSelect: (page: number) => void;
    };

const SHOW_MORE_MODE: ListingPaginationMode = { kind: 'showMore' };

interface PropertyListProps {
  isLoading: boolean;
  isLoadingMore: boolean;
  handleOnSeeMore: () => void;
  listings?: MarketplacePropertyList;
  likedPropertyIds?: LikedPropertyIds;
  initialFavoritesPending?: boolean;
  onFavoriteChange?: MarketplaceFavoriteChangeHandler;
  firstPhotoLoadMode: PhotoLoadMode;
  pagination?: ListingPaginationMode;
}

export const PropertyListRefreshOverlay = ({
  children,
  isRefreshing,
}: PropsWithChildren<{ isRefreshing: boolean }>) => {
  // React 18 drops `inert` when it is passed as a boolean JSX prop.
  const setResultContentRef = useCallback(
    (element: HTMLElement | null) => {
      element?.toggleAttribute('inert', isRefreshing);
    },
    [isRefreshing],
  );

  return (
    <Col className="relative w-full" aria-busy={isRefreshing || undefined}>
      <Col ref={setResultContentRef} className="w-full">
        {children}
      </Col>
      {isRefreshing && <Box aria-hidden className="absolute inset-0 z-2 bg-od-bg-primary/72" />}
    </Col>
  );
};

export const PropertyList = ({
  isLoading,
  isLoadingMore,
  handleOnSeeMore,
  listings,
  likedPropertyIds,
  initialFavoritesPending,
  onFavoriteChange,
  firstPhotoLoadMode,
  pagination = SHOW_MORE_MODE,
}: PropertyListProps) => {
  const propertyCards = useMemo(() => {
    return listings?.listings.map((listing, index) => {
      return (
        <Box key={listing.addressId}>
          <ListingCard
            listing={listingCardData(listing, likedPropertyIds)}
            favoriteOrigin="GALLERY"
            initialFavoritePending={initialFavoritesPending}
            onFavoriteChange={onFavoriteChange}
            photoLoadMode={index === 0 ? firstPhotoLoadMode : 'deferred'}
            photoMode="gallery"
          />
        </Box>
      );
    });
  }, [listings, likedPropertyIds, initialFavoritesPending, onFavoriteChange, firstPhotoLoadMode]);

  if (isLoading && listings == null) {
    return (
      <Row className="w-full items-center justify-center">
        <Loader aria-label="Loading homes" size="lg" />
      </Row>
    );
  }

  return (
    <>
      <Box className="grid grid-cols-1 gap-6 lg:grid-cols-2">{propertyCards}</Box>
      {pagination.kind === 'showMore' ? (
        listings?.nextPage != null && (
          <ShowMore onShowMore={handleOnSeeMore} isLoading={isLoadingMore} />
        )
      ) : (
        <PaginationActions
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          hrefForPage={pagination.hrefForPage}
          onPageSelect={pagination.onPageSelect}
          // `isLoadingMore` tracks Show more's fetchNextPage; a page change runs
          // through runSearch, so this is the flag that is actually in flight.
          isLoading={isLoading}
        />
      )}
    </>
  );
};
